import { Brand, ClaimViewModel } from './claim-types';

/**
 * An encapsulation of a single claim filter criterion. This class handles the following dirty work,
 * allowing a Vue component to focus its code on just the UI:
 *
 * - Keeps an array of all possible options (raw values and their human-friendly text).
 * - Maintains the currently selected option, ensuring it is valid before accepting it.
 * - Reads and writes the selected option in local storage, falling back to a default option if
 *   necessary.
 *
 * Subclasses need only override the `filter()` method, which determines whether a claim meets the
 * criterion of currently selected option.
 */
abstract class ClaimFilter<TOption extends string> {
	protected constructor(
		private readonly _storageKey: string,
		public readonly options: ReadonlyArray<[TOption, string]>,
		private _defaultOption: TOption = options[0][0],
	) {
		this.throwIfInvalidOption('_defaultOption', _defaultOption);
	}

	public get selectedOption(): TOption {
		if (this._selectedOption === undefined) {
			const option = window.localStorage.getItem(this._storageKey) as TOption;
			this._selectedOption = this.isValidOption(option) ? option : this._defaultOption;
		}
		return this._selectedOption;
	}
	public set selectedOption(option: TOption) {
		this.throwIfInvalidOption('option', option);
		window.localStorage.setItem(this._storageKey, option);
		this._selectedOption = option;
	}

	public abstract filter(claim: ClaimViewModel): boolean;

	private isValidOption(option: TOption): boolean {
		return this.options.some(
			(optionTuple: [TOption, string]): boolean => optionTuple[0] === option,
		);
	}

	private throwIfInvalidOption(paramName: string, option: TOption): void {
		if (!this.isValidOption(option)) {
			const validOptions: TOption[] = this.options.map(
				(optionTuple: [TOption, string]): TOption => optionTuple[0],
			);
			throw new TypeError(
				`Argument '${paramName}' specified value ${JSON.stringify(
					option,
				)}, which is not one of ${JSON.stringify(validOptions)}`,
			);
		}
	}

	private _selectedOption: TOption | undefined;
}

export type ClaimBrandFilterOption = 'all' | 'unassigned' | Brand;

export class ClaimBrandFilter extends ClaimFilter<ClaimBrandFilterOption> {
	public constructor() {
		super(
			'claimBrandFilter',
			// prettier-ignore
			[
                    ['all',             'Any'],
                    ['insurecareteams', 'InsureCAREteams'],
                    ['mmic',            'MMIC'],
                    ['umia',            'UMIA'],
                    ['amic',            'AMIC'],
                    ['unassigned',      'Not specified'],
               ],
		);
	}

	public filter(claim: ClaimViewModel): boolean {
		switch (this.selectedOption) {
			case 'all':
				return true;

			case 'unassigned':
				return !claim.brand;

			default:
				return claim.brand === this.selectedOption;
		}
	}
}

export type ClaimSetupFilterOption = 'all' | 'notSetup' | 'setup';

export class ClaimSetupFilter extends ClaimFilter<ClaimSetupFilterOption> {
	public constructor() {
		super(
			'claimSetupFilter',
			// prettier-ignore
			[
                    ['all',      'Yes or no'],
                    ['setup',    'Yes'],
                    ['notSetup', 'No'],
               ],
		);
	}

	public filter(claim: ClaimViewModel): boolean {
		switch (this.selectedOption) {
			case 'all':
				return true;

			case 'notSetup':
				return claim.setup !== true;

			case 'setup':
				return claim.setup === true;
		}
	}
}
